import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";

import { AppointmentExchangeResponse } from "@/api";
import { ExchangeRequestedServicesValues } from "@/pages/exchange/Exchange.types";
import { useExchangeRequestMutations } from "@/pages/exchange/hooks/useExchangeRequestMutations";
import { useRequestablePurposes } from "@/pages/exchange/hooks/useRequestablePurposes";
import { useTranslation } from "@/translations";

import { editExchangeRequestValidationSchema } from "../EditExchangeRequestDialog.utils";

interface Params {
  exchange: AppointmentExchangeResponse;
  onClose: () => void;
}

/**
 * Form state of the "Tauschanfrage bearbeiten" dialog. The offered appointment cannot change, so only the
 * wanted purposes (gesuchte Dienste) and the note (Notiz) are editable; offers already made stay on the request.
 */
export function useEditExchangeRequest({ exchange, onClose }: Params) {
  const { t } = useTranslation();
  const purposes = useRequestablePurposes();
  const { updateRequest } = useExchangeRequestMutations();

  const validationSchema = useMemo(() => editExchangeRequestValidationSchema(t), [t]);

  // A purpose switched off since the request was published cannot be asked for any more, so the form
  // starts without it and saving drops it; the backend would reject the request otherwise.
  const requestableKeys = exchange.requestedPurposeKeys.filter((key) =>
    purposes.options.some((option) => option.key === key)
  );

  const formik = useFormikWithDirtyForm<ExchangeRequestedServicesValues>({
    initialValues: { requestedPurposeKeys: requestableKeys, note: exchange.note ?? "" },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const note = values.note.trim();
      try {
        await updateRequest.mutateAsync(
          {
            exchangeId: exchange.id,
            request: { requestedPurposeKeys: values.requestedPurposeKeys, note: note === "" ? null : note },
          },
          { onSuccess: onClose }
        );
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  const submitDisabled = formik.values.requestedPurposeKeys.length === 0 || updateRequest.isPending;

  return { formik, submitDisabled };
}
